import { Grid } from '@mui/material';
import iban from 'iban';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';
import { DateTimeInputPicker } from '@work4all/components/lib/input/date-time-input-picker';

import { useFormPlus } from '@work4all/data';

import { BankDetails } from '@work4all/models/lib/Classes/BankDetails.entity';
import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { Supplier } from '@work4all/models/lib/Classes/Supplier.entity';

import { useJSONSchemaResolver } from '@work4all/utils';

import { ControllerPlus } from '../../../../../../../../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../../../../../../../../form-plus/use-form-context-plus';
import {
  CheckboxRadioItem,
  LabeledInput,
} from '../../../../../../../../../../locked-inputs';
import { MaskPopover } from '../../../../../../../../../components/mask-popover/MaskPopover';
import { useMaskContext } from '../../../../../../../../../hooks/mask-context';

export const BankDetailsPopover: React.FC<{
  selectedElement?: BankDetails;
  onSubmit: (result: BankDetails) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  popoverState: any;
  newBankList?: BankDetails[];
}> = (props) => {
  const { t } = useTranslation();
  const { selectedElement, newBankList, onSubmit, popoverState } = props;

  const { data } = useMaskContext<Customer | Supplier>();
  const defaultValues = useMemo(() => {
    if (!data?.bankDetailsList?.length && !newBankList.length) {
      return { ...selectedElement, isMainBank: true };
    }

    return selectedElement ?? {};
  }, [data?.bankDetailsList?.length, newBankList.length, selectedElement]);

  const customRules = useCallback(
    (data: BankDetails) => {
      const errors = {};

      if (!data.iban) {
        errors['iban'] = {
          message: t('COMMON.MANDATORY_FIELD'),
          type: 'customValidation',
        };
      } else if (!iban.isValid(data.iban)) {
        errors['iban'] = {
          message: t('VALIDATION.IBAN_INVALID'),
          type: 'customValidation',
        };
      }

      if (data.accountNumber?.length > 35) {
        errors['accountNumber'] = {
          message: t('VALIDATION.ACCOUNT_NUMBER_TOO_LONG'),
          type: 'customValidation',
        };
      }

      return Object.keys(errors).length > 0 ? errors : true;
    },
    [t]
  );

  const resolver = useJSONSchemaResolver(undefined, customRules);

  const form = useFormPlus<BankDetails>({
    resolver,
    defaultValues,
    mode: 'onSubmit',
    shouldFocusError: false,
  });

  const { handleSubmit, formState } = form;

  useEffect(() => form.reset(defaultValues), [form, defaultValues]);

  const isDirty = formState.isDirty;

  return (
    <MaskPopover
      title={t('MASK.BANK_DETAILS')}
      submit={handleSubmit((data) => {
        onSubmit(data);
        popoverState.onClose();
      })}
      cancel={popoverState.onClose}
      anchorEl={popoverState.anchorEl}
      open={popoverState.open}
      disableSubmission={!isDirty}
    >
      <FormProvider {...form}>
        <BankDetailsPopoverContent />
      </FormProvider>
    </MaskPopover>
  );
};

const BankDetailsPopoverContent = () => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContextPlus<BankDetails>();

  return (
    <Grid container spacing="1rem">
      <Grid item xs={12}>
        <Divider title={t('COMMON.MANDATORY_FIELD', { count: 2 })} />
      </Grid>

      <Grid item xs={12}>
        <ControllerPlus
          control={control}
          name="accountOwner"
          render={({ field }) => {
            return (
              <LabeledInput
                {...field}
                required
                label={t('INPUTS.ACCOUNT_OWNER')}
              />
            );
          }}
        />
      </Grid>
      <Grid item xs={8}>
        <ControllerPlus
          control={control}
          name="iban"
          render={({ field }) => {
            return (
              <LabeledInput
                {...field}
                label={t('INPUTS.IBAN')}
                required
                error={errors.iban?.message}
              />
            );
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <ControllerPlus
          control={control}
          name="bic"
          render={({ field }) => {
            return <LabeledInput {...field} label={t('INPUTS.BIC_OPTIONAL')} />;
          }}
        />
      </Grid>
      <Grid item xs={8}>
        <ControllerPlus
          control={control}
          name="mandateReferenceNumber"
          render={({ field }) => {
            return (
              <LabeledInput
                {...field}
                label={t('INPUTS.SEPA_MANDATE_REF_NUMBER')}
              />
            );
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <ControllerPlus
          control={control}
          name="mandateDate"
          render={({ field }) => {
            return (
              <DateTimeInputPicker
                {...field}
                dateLabel={t('INPUTS.MANDATE_DATE')}
                withTime={false}
              />
            );
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Divider title={t('COMMON.OPTIONAL_FIELD', { count: 2 })} />
      </Grid>

      <Grid item xs={12}>
        <ControllerPlus
          control={control}
          name="name"
          render={({ field }) => {
            return (
              <LabeledInput
                {...field}
                label={t('INPUTS.CREDIT_INSTITUTION')}
                error={errors.name?.message}
              />
            );
          }}
        />
      </Grid>

      <Grid item xs={8}>
        <ControllerPlus
          control={control}
          name="accountNumber"
          render={({ field }) => {
            return (
              <LabeledInput
                {...field}
                label={t('INPUTS.ACCOUNT_NUMBER')}
                error={errors.accountNumber?.message}
              />
            );
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <ControllerPlus
          control={control}
          name="bankSortCode"
          render={({ field }) => {
            return <LabeledInput {...field} label={t('INPUTS.BLZ')} />;
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <ControllerPlus
          control={control}
          name="isMainBank"
          render={({ field }) => {
            return (
              <CheckboxRadioItem
                label={t('INPUTS.STANDART_CUSTOMER_BANK_ACC')}
                checked={field.value}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                }}
              />
            );
          }}
        />
      </Grid>
    </Grid>
  );
};
